import { auth } from '../../constants/firebaseModular';

/**
 * Authentication-assurance level (AAL) — the H-A contract, ported from
 * `mideo-health-app` (CU-86ajnjka0 / CU-86ajwpnek §9).
 *
 * THE SINGLE PHI-ACCESS GATE is `aal2 === true`, satisfied by EITHER:
 *   - an Identity-Platform native-MFA (SMS) session — what the portal ships
 *     today, and
 *   - the passkey custom claim `{ mfa: "passkey", aal2: true }` — minted by the
 *     passkey bridge, because `signInWithCustomToken` BYPASSES Identity
 *     Platform's native MFA challenge and so cannot be recognised by asking
 *     Firebase.
 *
 * ⚠️ BOTH BRANCHES ARE LIVE. THE PASSKEY BRANCH IS NO LONGER UNREACHABLE —
 * do NOT "simplify" it away.
 *
 * This comment previously read "the portal has no passkey path yet, so the
 * first branch is currently unreachable". That was true when written and is
 * FALSE AS OF CU-86ajzr94n (sub-task 3), which shipped passkey login on
 * `/login`. Corrected during that task's adversarial round, because the diff
 * that falsifies a claim is where the claim should be fixed.
 *
 * What makes it live: `usePasskeyLogin` exchanges a server-issued custom token
 * via `AuthContext.firebaseSignInWithCustomToken`, and the bridge mints
 * `{ mfa: "passkey", aal2: true }` on that token (verified in
 * `mideo-cloud-functions/src/auth/passkey/passkeyVerify.ts`, `PASSKEY_MFA_CLAIMS`).
 * So a portal-originated passkey session now takes the first branch below.
 * Mobile-enrolled passkeys carried into a portal session take it too, which was
 * always the reason to read it.
 *
 * ⚠️ DELETING IT GATES EVERY PASSKEY PATIENT OUT OF THEIR OWN RECORDS, and it
 * fails in the SAFE-LOOKING direction — a reader that only knows SMS reports
 * them as AAL1, which does not read as a crash. It reads as policy.
 *
 * ⚠️ Deliberately reads the DECODED TOKEN, never a local flag. The claim is
 * server-minted and signed, so it cannot be forged client-side. Never infer
 * AAL2 from "we called a sign-in function" — that would let a password-only
 * path launder itself into an AAL2-looking session.
 *
 * ✅ MEASURED CLAIM SHAPE (2026-08-05, staging, checklist §B): after an SMS
 * enrollment or challenge the token carries `firebase.sign_in_second_factor =
 * "phone"` NESTED inside the `firebase` block, with `sign_in_provider =
 * "password"`. Top-level `sign_in_second_factor`, `aal2` and `mfa` are ABSENT
 * on that path — the "H1 trap", confirmed empty. Reading the wrong path gates
 * every SMS patient out of their PHI, so the shape was measured rather than
 * assumed.
 */

/** How the session reached AAL2, when it did. */
export type Aal2Method = 'passkey' | 'mfa' | null;

export interface AuthAssurance {
  /** True when the session is MFA-satisfied (IdP MFA OR the passkey claim). */
  aal2: boolean;
  /** Which factor got it there, for telemetry and UI copy. */
  method: Aal2Method;
}

/**
 * Mirror an OBSERVED assurance into the sync cache, then pass it through.
 *
 * ⚠️ Only called where a real token answer was reached — never from the catch,
 * where "we don't know" must not be recorded as "not AAL2". See the mirror's
 * own note at the bottom of this file.
 */
function remember(a: AuthAssurance): AuthAssurance {
  rememberAal2(a.aal2);
  return a;
}

/**
 * Read the current session's assurance from its ID token claims.
 *
 * `forceRefresh` is false by default: the cached token is fine immediately
 * after sign-in (it was just minted). Pass true when the claim may have changed
 * server-side — e.g. right after enrolling a second factor.
 *
 * Never throws: on any failure it reports "not AAL2", which is the safe
 * direction for a gate.
 */
export async function getAuthAssurance(forceRefresh = false): Promise<AuthAssurance> {
  try {
    const user = auth.currentUser;
    if (!user) return remember({ aal2: false, method: null });

    const result = await user.getIdTokenResult(forceRefresh);
    const claims = (result.claims ?? {}) as Record<string, unknown>;

    // Passkey path — the claim the bridge mints (H-A). REACHABLE FROM THE
    // PORTAL as of CU-86ajzr94n, and also from a mobile-enrolled passkey
    // carried into a portal session. See the header.
    if (claims.aal2 === true) {
      return remember({ aal2: true, method: claims.mfa === 'passkey' ? 'passkey' : 'mfa' });
    }

    // Identity-Platform native MFA (SMS). ⚠️ NESTED inside `firebase`, not
    // top-level — measured, not assumed.
    const firebaseClaim = claims.firebase as { sign_in_second_factor?: string } | undefined;
    if (firebaseClaim?.sign_in_second_factor) {
      return remember({ aal2: true, method: 'mfa' });
    }

    return remember({ aal2: false, method: null });
  } catch {
    // ⚠️ NOT MIRRORED. A failed read is "we don't know", not "not AAL2" —
    // writing false here would let a transient token-read failure silence a
    // genuine post-deploy rules defect for a session that IS AAL2. The mirror
    // keeps its last real answer; only an observed claim moves it.
    return { aal2: false, method: null };
  }
}

/** Convenience predicate. */
export async function isAal2(forceRefresh = false): Promise<boolean> {
  return (await getAuthAssurance(forceRefresh)).aal2;
}

/**
 * ── SYNCHRONOUS MIRROR OF THE LAST KNOWN ASSURANCE (CU-86ak8ytkt) ─────────
 *
 * ⚠️ EXISTS FOR ONE REASON: `useFirestoreListener`'s error handler is SYNC, and
 * the reader above is ASYNC. A listener error cannot `await` a token read — the
 * handler runs inside Firestore's onSnapshot error callback, and making it
 * async would either drop the error on the floor or reorder it against the
 * retry/backoff bookkeeping that follows it.
 *
 * ⚠️ THIS IS A TELEMETRY-SUPPRESSION INPUT ONLY. It MUST NOT be used to gate
 * PHI, mint a session, or decide a route. Those all read `getAuthAssurance`
 * directly, against the signed token, with `forceRefresh` where it matters. A
 * cached boolean is forgeable by anything that can call the setter, so it is
 * unfit to be a security control and is deliberately not exported as one.
 *
 * ⚠️ DEFAULTS TO FALSE, AND THAT DIRECTION IS CHOSEN. Before any assurance read
 * completes, the mirror reads "not AAL2" — so the suppression in
 * `useFirestoreListener` does NOT engage and the event IS reported. An unknown
 * session therefore behaves like the loud case, never the silent one: we would
 * rather over-report during the first moments of a session than blind ourselves
 * to a real rules defect.
 */
let lastKnownAal2 = false;

/**
 * Record the assurance just read from a token. Called by the async readers so
 * the sync mirror tracks the real answer.
 */
export function rememberAal2(aal2: boolean): void {
  lastKnownAal2 = aal2;
}

/**
 * The last assurance observed on this session, synchronously.
 *
 * ⚠️ NOT A SECURITY BOUNDARY — see `rememberAal2`. Read this only to decide
 * whether an observation is worth REPORTING.
 */
export function lastKnownIsAal2(): boolean {
  return lastKnownAal2;
}

/** Test seam. ⚠️ Never called from app code. */
export function __resetAssuranceMirrorForTest(): void {
  lastKnownAal2 = false;
}
